const article = {
	slug: "compliance-as-discipline",
	title: "Compliance as Discipline, Not Box-Ticking",
	date: "18 April 2026",
	updated: null,
	description:
		"SR 11-7 was written for stable, explainable models. AI models drift and find proxies nobody programmed. If your model validation does not touch bias testing, you do not have a documentation gap. You have a fair lending exposure.",
	keywords: [
		"model risk",
		"SR 11-7",
		"AI bias",
		"fair lending",
		"disparate impact",
		"AI governance",
	],
	tldr: "Model risk regulation is real engineering risk, not paperwork. Most teams treat SR 11-7 as a documentation exercise, which works for a regression model and fails for AI that drifts and encodes protected characteristics nobody flagged.",
	readingTime: null,
	provenance: "Adapted from my LinkedIn post, April 2026.",
	provenanceUrl: null,
	series: "ai-governance-for-finserv-leaders",
	artifact: null,
	markdown: `I build data pipelines. I am not the model risk officer.

But fifteen years in FinServ engineering puts you close enough to see the gaps.

I sat through a demo last week. A publicly available underwriting dataset from 2015, tested against a current population. The model was under-classifying risk. In a real deployment, that is premium left on the table every single day, with no alarm going off.

That is model drift. And there is a regulatory framework built exactly to catch it.

## The framework was written for a different kind of model

SR 11-7 has governed how banks validate quantitative models since 2011. Most teams treat it as a documentation exercise. Model inventory. Independent validation. Annual review. Box checked.

That works fine for a regression model. It does not work for AI.

SR 11-7 was written for stable, explainable models. Explicit coefficients. Known inputs. ML models drift. They find proxies nobody programmed. They can encode protected characteristics through zip code, device type, spending patterns, and nobody flags it until someone goes looking.

Since then, the 2021 Interagency AI/ML Statement, CFPB Circular 2022-03, and the NIST AI RMF have each extended the same expectation: you need to know what your model is doing to the people it touches.

Disparate impact testing is now a regulatory obligation. Not a DEI initiative.

## Nobody owns the intersection

Here is what I see from the engineering side. The model risk team owns SR 11-7. The fair lending team owns ECOA. The AI team owns the model. Nobody owns the intersection.

The validation report gets filed. The bias section says "not applicable." The exposure sits there.

I asked a question in one of these reviews that changed the room. Not about documentation. About output: "Have you tested the distribution across protected classes?"

That is SR 11-7 working the way it was intended.

## Governance keeps you out of a consent order

Governance does not slow AI down in FinServ. It keeps you out of a consent order.

If your model validation does not touch bias testing, you don't have an SR 11-7 gap. You have a fair lending exposure.`,
};

export default article;
